import type { db } from '@funkey/api/src';
import type { ts } from '@funkey/shared';
import { Order, SortedSet } from 'effect';
import { z } from 'zod';

/** Canonical schema for a bizz's catalog rules payload — the single source of
 * truth shared by the server action `.input()` and the client edit-mode
 * session. Mirrors the `bizz.activityFilters` column `$type`
 * (`business.schema.ts`) plus `automaticallyAddNewActivitiesToSelection`,
 * nested under `activityFilters` exactly like the column.
 *
 * `budget.max`/`participants.max` are required-and-nullable here (the client
 * canonical form, so `===` dirty-detection stays stable) rather than the
 * column's optional `max?`. A required-null value is assignable to an optional
 * field, so this stays compatible with the column type. */
export const Catalog_EditMode_RulesSchema = z.object({
    automaticallyAddNewActivitiesToSelection: z.boolean(),
    activityFilters: z.object({
        rootCategoryIDs: z.number().array().nullable(),
        activityRegionIDs: z.number().array().nullable(),
        budget: z.object({
            min: z.number().nullable(),
            max: z.number().nullable(),
        }),
        participants: z.object({
            min: z.number().nullable(),
            max: z.number().nullable(),
        }),
        groupIDs: z.object({
            mode: z.enum(['INCLUSIVE', 'EXCLUSIVE']),
            IDs: z.number().array().nullable(),
        }),
    }),
} satisfies Partial<ts.Zod.InputShape<typeof db.businesses.$inferInsert>>);

/** Editable rules shape — single source of truth for the Rules pane,
 * `Catalog_EditMode_ModeSwitch`, sticky footer, and per-card add/remove
 * buttons. Nested to mirror the `bizz.activityFilters` column. */
export type Catalog_EditMode_Rules = z.infer<typeof Catalog_EditMode_RulesSchema>;

/** ID arrays are written through `SortedSet` at every mutation site (card
 * toggle, rule-allowlist toggle). Normalising the server-rendered seed the
 * same way puts the dirty-baseline in canonical ascending order, so
 * react-form's positional `isDefaultValue` stays accurate after add/remove
 * sequences that net to zero. Preserves `null` (filter off) and `[]` (matches
 * nothing) distinctly per the `Catalog_RuleIDs` convention. */
export function sortIds(ids: number[] | null): number[] | null {
    if (ids === null) return null;
    return Array.from(SortedSet.fromIterable(ids, Order.number));
}

/** Read rule for the category/region allowlists (`Catalog_RuleIDs` convention):
 * `null` = "filter off", a non-empty array = allowlist. `[]` is unreachable from
 * {@link resolveAllowlistToggle}, but legacy rows hold it meaning "no
 * restriction" — normalise on read so both spellings mean the same thing. */
export function canonicalAllowlistIDs(ids: number[] | null): number[] | null {
    return ids === null || ids.length === 0 ? null : ids;
}

/** {@link canonicalAllowlistIDs} applied to both allowlists of a rules payload,
 * for the seam where a stored `bizz.activityFilters` enters the client. */
export function canonicalRuleAllowlists<
    T extends {
        rootCategoryIDs: number[] | null;
        activityRegionIDs: number[] | null;
    },
>(activityFilters: T): T {
    return {
        ...activityFilters,
        rootCategoryIDs: canonicalAllowlistIDs(activityFilters.rootCategoryIDs),
        activityRegionIDs: canonicalAllowlistIDs(activityFilters.activityRegionIDs),
    };
}

/** The `groupIDs.IDs` write rule (`Catalog_RuleIDs` convention). Sorts +
 * dedupes, then applies mode-aware empty handling: EXCLUSIVE-empty collapses
 * to `null` ("nothing excluded = filter off"); INCLUSIVE-empty stays `[]`
 * ("nothing selected yet → show nothing", realized via the mapper's `disabled`
 * short-circuit). Non-empty input is returned sorted + deduped. */
export function canonicalGroupIDs(
    mode: 'INCLUSIVE' | 'EXCLUSIVE',
    ids: number[] | null,
): number[] | null {
    const sorted = ids === null ? null : sortIds(Array.from(new Set(ids)));
    if (sorted === null || sorted.length === 0) {
        return mode === 'INCLUSIVE' ? [] : null;
    }
    return sorted;
}

/** Rules patch for a catalog-mode switch. Clearing `groupIDs.IDs` is mode-aware
 * (see `canonicalGroupIDs`). Switching to INCLUSIVE additionally lifts every
 * rule constraint the Rules pane surfaces — regions, categories, budget: the
 * catalog is then exactly the manually picked activities, so a leftover
 * region/category/budget rule could only silently hide picks the admin made on
 * purpose. Switching to EXCLUSIVE keeps the rules — there they *are* the
 * catalog. `participants` is round-tripped but not surfaced as a UI control, so
 * it is left untouched in both directions. */
export function resolveModeSwitch(
    mode: 'INCLUSIVE' | 'EXCLUSIVE',
    rules: Catalog_EditMode_Rules,
): Catalog_EditMode_Rules {
    return {
        ...rules,
        activityFilters: {
            ...rules.activityFilters,
            ...(mode === 'INCLUSIVE'
                ? {
                      rootCategoryIDs: null,
                      activityRegionIDs: null,
                      budget: { min: null, max: null },
                  }
                : {}),
            groupIDs: { mode, IDs: canonicalGroupIDs(mode, []) },
        },
    };
}

/** Pure decision for a category/region allowlist toggle (`null = all active`).
 * Given the full set of selectable IDs, the current allowlist (`null` = all),
 * and the toggled `id`, returns the next allowlist value. Both saturation (all
 * selected) AND deselect-last (none remain) collapse to `null`, keeping `[]`
 * unreachable for these allowlists per the `Catalog_RuleIDs` convention. Output
 * arrays are sorted ascending (via `sortIds`) so the session's positional
 * dirty-detection stays stable. */
export function resolveAllowlistToggle(
    allIds: readonly number[],
    current: readonly number[] | null,
    id: number,
): number[] | null {
    const toggled = SortedSet.toggle(
        SortedSet.fromIterable(current === null ? allIds : current, Order.number),
        id,
    );
    const arr = sortIds(Array.from(toggled));
    const allSelected =
        (arr?.length ?? 0) === allIds.length && allIds.every(x => SortedSet.has(toggled, x));
    return allSelected || (arr?.length ?? 0) === 0 ? null : arr;
}
